import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

interface CountdownTimerProps {
    startTime: string; // Example: "3-17-2025 09:55"
    durationMinutes: number; // Example: 20
    timeUp?: () => void; // Callback function when time is up
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ startTime, durationMinutes, timeUp }) => {
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const totalDuration = durationMinutes * 60 * 1000;
    const timeUpCalledRef = React.useRef(false);

    useEffect(() => {
        if (!startTime || durationMinutes <= 0) {
            return;
        }

        const startDate = dayjs(startTime).toDate();
        const endTime = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

        const updateTimer = () => {
            const now = new Date();
            const diff = Math.max(0, endTime.getTime() - now.getTime());
            setTimeLeft(diff);

            if (diff <= 0 && !timeUpCalledRef.current && timeUp) {
                console.log('CountdownTimer: Time is up, calling callback');
                timeUpCalledRef.current = true;
                timeUp();
            }
        };

        const now = new Date();
        const initialDiff = endTime.getTime() - now.getTime();

        if (initialDiff <= 0) {
            console.warn('CountdownTimer: Timer already expired. Start time:', startTime, 'Duration:', durationMinutes, 'End time:', endTime, 'Now:', now);
            if (!timeUpCalledRef.current && timeUp) {
                timeUpCalledRef.current = true;
                timeUp();
            }
            return;
        }

        updateTimer();
        const timerInterval = setInterval(updateTimer, 1000);

        return () => clearInterval(timerInterval);
    }, [startTime, durationMinutes, timeUp]);

    const formatTime = (ms: number) => {
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    const isWarning = timeLeft <= 5 * 60 * 1000 && timeLeft > 0;
    const secondsLeftInMinute = (timeLeft / 1000) % 60;
    const progressInSeconds = 60 - secondsLeftInMinute;

    const segment = Math.floor(progressInSeconds / 15);
    const percentInSegment = ((progressInSeconds % 15) / 15) * 100;
    const color = isWarning ? "bg-red-500" : "bg-blue-500";

    return (
        <div className="relative w-20 h-10">
            {/* Top border: Left to Right */}
            <div
                className={`${color} absolute top-0 left-0 h-1`}
                style={{
                    width: segment >= 1 ? "100%" : segment === 0 ? `${percentInSegment}%` : "0%",
                }}
            />
            {/* Right border: Top to Bottom */}
            <div
                className={`${color} absolute top-0 right-0 w-1`}
                style={{
                    height: segment >= 2 ? "100%" : segment === 1 ? `${percentInSegment}%` : "0%",
                }}
            />
            {/* Bottom border: Right to Left */}
            <div
                className={`${color} absolute bottom-0 right-0 h-1`}
                style={{
                    width: segment >= 3 ? "100%" : segment === 2 ? `${percentInSegment}%` : "0%",
                }}
            />
            {/* Left border: Bottom to Top */}
            <div
                className={`${color} absolute bottom-0 left-0 w-1`}
                style={{
                    height: segment >= 4 ? "100%" : segment === 3 ? `${percentInSegment}%` : "0%",
                }}
            />

            {/* Timer Text */}
            <div
                className={`absolute inset-0 flex items-center justify-center  ${timeLeft > 0 ? 'text-2xl' : 'text-md'}  font-bold ${isWarning ? "text-red-600" : "text-black"
                    }`}
            >
                {timeLeft > 0 ? formatTime(timeLeft) : "Time's up!"}
            </div>
        </div>
    );
};

export default CountdownTimer;
